import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Bug, Smartphone, Code, Brain, Sparkles, Cloud, GitBranch,
  Cpu, CircuitBoard, Settings2, ArrowRight, Check, Layers,
  MapPin, Wifi, Users, Award, BookOpen, GraduationCap,
  Zap, Clock, Star, Mail, Phone, Building2, BadgeCheck,
  CalendarDays, Handshake, Tag, LayoutGrid,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.48, ease: 'easeOut' } },
};
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.07 } } };

/* ── DATA ───────────────────────────────────────── */
const domains = [
  { num: '01', icon: Bug,         color: '#f43f5e', name: 'Software Testing',            tag: 'QA & Testing',      wide: false, badge: null,       tools: ['Selenium', 'Postman', 'JIRA', 'TestNG'] },
  { num: '02', icon: Smartphone,  color: '#a855f7', name: 'Mobile App Development',      tag: 'Android · iOS',     wide: false, badge: 'HOT',       tools: ['Flutter', 'React Native', 'Firebase', 'Swift'] },
  { num: '03', icon: Code,        color: '#3b82f6', name: 'Full Stack Web Development',  tag: 'Most Popular',      wide: true,  badge: 'TOP PICK',  tools: ['React.js', 'Node.js', 'Express', 'MongoDB', 'MySQL'], headline: 'Build complete web applications — pixel-perfect UIs to scalable APIs.' },
  { num: '04', icon: Brain,       color: '#14b8a6', name: 'AI & Machine Learning',       tag: 'Data Science',      wide: true,  badge: 'TRENDING',  tools: ['Python', 'TensorFlow', 'Scikit-learn', 'Pandas', 'NumPy'], headline: 'From raw data and feature engineering to deployed, production ML models.' },
  { num: '05', icon: Sparkles,    color: '#8b5cf6', name: 'Generative AI (GenAI)',       tag: 'LLMs · Prompts',    wide: false, badge: 'NEW',       tools: ['LangChain', 'OpenAI', 'RAG', 'Hugging Face'] },
  { num: '06', icon: Cloud,       color: '#0ea5e9', name: 'Cloud Computing',             tag: 'AWS · Azure · GCP', wide: false, badge: null,        tools: ['AWS', 'Azure', 'GCP', 'Lambda', 'S3'] },
  { num: '07', icon: GitBranch,   color: '#f97316', name: 'DevOps & MLOps',              tag: 'Automation',        wide: false, badge: null,        tools: ['Docker', 'Kubernetes', 'Jenkins', 'CI/CD'] },
  { num: '08', icon: Cpu,         color: '#22c55e', name: 'IoT & Robotics',              tag: 'Hardware',          wide: false, badge: null,        tools: ['Arduino', 'Raspberry Pi', 'MQTT', 'Node-RED'] },
  { num: '09', icon: CircuitBoard,color: '#f59e0b', name: 'Embedded Systems',            tag: 'Firmware',          wide: false, badge: null,        tools: ['C/C++', 'ARM Cortex', 'RTOS', 'STM32'] },
  { num: '10', icon: Settings2,   color: '#6366f1', name: 'PLC & Industrial Automation', tag: 'Industrial',        wide: false, badge: null,        tools: ['Ladder Logic', 'Siemens S7', 'SCADA', 'HMI'] },
];

const durations = [
  { days: '7',  unit: 'Days',   label: 'Crash Course',         sub: 'Fast skill injection', popular: false },
  { days: '15', unit: 'Days',   label: 'Short-Term Intensive', sub: 'Focused domain depth', popular: false },
  { days: '1',  unit: 'Month',  label: 'Certificate Program',  sub: 'Full domain coverage', popular: false },
  { days: '3',  unit: 'Months', label: 'Diploma Program',      sub: 'End-to-end mastery',   popular: true  },
];

const deliveryModes = [
  { icon: MapPin, label: 'In-Campus (Offline)',  color: '#3b82f6', desc: 'Expert trainers at your premises. Hands-on labs, real-time feedback, immersive environment.' },
  { icon: Wifi,   label: 'Online / Remote',      color: '#8b5cf6', desc: 'Live instructor-led sessions. Recorded playbacks, digital assessments, project submissions.' },
  { icon: Layers, label: 'Hybrid (Recommended)', color: '#f97316', desc: 'Foundation in-campus + advanced online. Proven at Acharya Polytechnic (1 mo. on-site + 2 mo. online).', badge: true },
];

const institutionalBenefits = [
  { icon: Tag,          title: 'Custom Pricing for Bulk Batches',       desc: 'Special rates for batches of 30+ students. Tiered pricing based on cohort size and program duration.' },
  { icon: LayoutGrid,   title: 'Curriculum Aligned to Your Syllabus',   desc: 'We tailor content to your academic calendar, existing course structure, and placement objectives.' },
  { icon: Handshake,    title: 'Multi-Domain Coverage Under One Roof',  desc: 'One partner for all 10 technology domains — Software Testing, AI/ML, Web Dev, DevOps, and more.' },
  { icon: CalendarDays, title: 'Flexible Batch Scheduling',             desc: 'Semester breaks, weekends, elective slots — we adapt to your academic timetable, not the other way.' },
  { icon: BadgeCheck,   title: 'Co-Branded or CALLOC Certificates',     desc: 'Choose CALLOC-certified programs or request co-branded certificates with your institution.' },
  { icon: Users,        title: 'Dedicated Training Coordinator',        desc: 'A single point of contact from inquiry to certification — no handoffs, no confusion.' },
];

const engagements = [
  { name: 'Acharya Polytechnic College',             loc: 'Bangalore, KA', type: 'Hybrid',      duration: '3 Months',  students: '80+' },
  { name: 'Sri Eswar College of Engineering',         loc: 'Tamil Nadu',    type: 'In-Campus',   duration: '16 Days',   students: '120+' },
  { name: 'TN Skills · Karnataka Skills · India Skills', loc: 'Pan India', type: 'Collaborative', duration: 'Ongoing',  students: '300+' },
];

const internshipTracks = [
  { icon: Code,       color: '#3b82f6', name: 'Full Stack Web',         duration: '2–3 Months', level: 'Intermediate', spots: 8,  skills: ['React', 'Node.js', 'MongoDB', 'MySQL'] },
  { icon: Smartphone, color: '#a855f7', name: 'Mobile App Development', duration: '2–3 Months', level: 'Intermediate', spots: 6,  skills: ['Flutter', 'Firebase', 'Dart', 'APIs'] },
  { icon: Brain,      color: '#14b8a6', name: 'AI & Data Science',      duration: '2 Months',   level: 'Advanced',     spots: 4,  skills: ['Python', 'ML', 'TensorFlow', 'Pandas'] },
  { icon: Bug,        color: '#f43f5e', name: 'Software Testing & QA',  duration: '1–2 Months', level: 'Beginner+',    spots: 10, skills: ['Selenium', 'Postman', 'JIRA', 'TestNG'] },
];

const internshipSteps = [
  { n: '01', title: 'Apply',          desc: 'Fill the registration form. We review your profile and domain preference within 48 hours.' },
  { n: '02', title: 'Shortlisting',   desc: 'Selected candidates are contacted for a brief 15-min intro call to assess fit and set expectations.' },
  { n: '03', title: 'Onboarding',     desc: 'Domain assigned, team introduced, tools set up. You join an active CALLOC project from day one.' },
  { n: '04', title: 'Build & Mentor', desc: 'Work on live client projects with daily standups, weekly code reviews, and direct mentor access.' },
  { n: '05', title: 'Demo & Certify', desc: 'Present at Demo Day. Receive certificate + Letter of Recommendation from CALLOC Technologies.' },
];

const vsRows = [
  { label: 'Duration',    training: '7 Days – 3 Months',                internship: '1 – 3 Months' },
  { label: 'Focus',       training: 'Skill building',                   internship: 'Real project work' },
  { label: 'Output',      training: 'Portfolio-ready skills',           internship: 'Shipped product / feature' },
  { label: 'Certificate', training: 'Yes',                              internship: 'Yes + Letter of Recommendation' },
  { label: 'Stipend',     training: 'Not applicable',                   internship: 'Performance-based' },
  { label: 'Ideal for',   training: 'Students, professionals, colleges', internship: 'CS/IT students, fresh graduates' },
];

/* ── DOMAIN BENTO CARD ──────────────────────────── */
function DomainCard({ d }) {
  const Icon = d.icon;
  return (
    <motion.div
      variants={fadeUp}
      whileHover={{ y: -6, boxShadow: `0 20px 60px ${d.color}30, 0 0 0 1px ${d.color}50`, transition: { duration: 0.2 } }}
      className={`relative group bg-[#0C1628] rounded-2xl p-6 overflow-hidden cursor-default ${d.wide ? 'lg:col-span-2 md:col-span-2' : ''}`}
    >
      <div className="absolute top-0 left-6 right-6 h-px"
        style={{ background: `linear-gradient(90deg, transparent, ${d.color}90, transparent)` }} />
      <div className="absolute -top-10 -left-10 w-52 h-52 rounded-full blur-3xl opacity-0 group-hover:opacity-20 transition-opacity duration-500 pointer-events-none"
        style={{ background: d.color }} />
      <div className="absolute top-3 right-5 font-black text-7xl leading-none select-none pointer-events-none"
        style={{ color: `${d.color}12` }}>{d.num}</div>
      {d.badge && (
        <div className="absolute top-4 right-4">
          <span className="text-[10px] font-bold tracking-widest px-2.5 py-1 rounded-full border"
            style={{ color: d.color, borderColor: `${d.color}40`, background: `${d.color}15` }}>
            {d.badge}
          </span>
        </div>
      )}
      <div className="relative mt-6 mb-5 w-fit">
        <div className="absolute inset-0 blur-xl rounded-full opacity-40 group-hover:opacity-70 transition-opacity" style={{ background: d.color }} />
        <div className="relative w-14 h-14 rounded-xl flex items-center justify-center"
          style={{ background: `${d.color}22`, border: `1px solid ${d.color}45` }}>
          <Icon className="w-7 h-7" style={{ color: d.color }} />
        </div>
      </div>
      <h3 className="text-white font-bold text-lg leading-tight mb-0.5">{d.name}</h3>
      <p className="text-white/30 text-xs mb-3 font-medium uppercase tracking-wider">{d.tag}</p>
      {d.wide && d.headline && <p className="text-white/45 text-sm mb-4 leading-relaxed max-w-sm">{d.headline}</p>}
      <div className="flex flex-wrap gap-1.5">
        {(d.tools || []).slice(0, d.wide ? 5 : 4).map((t) => (
          <span key={t} className="text-xs px-2.5 py-1 rounded-lg font-medium"
            style={{ background: `${d.color}14`, color: `${d.color}cc`, border: `1px solid ${d.color}28` }}>
            {t}
          </span>
        ))}
        {(d.tools || []).length > (d.wide ? 5 : 4) && (
          <span className="text-xs px-2.5 py-1 rounded-lg text-white/25 bg-white/5 border border-white/10">
            +{d.tools.length - (d.wide ? 5 : 4)}
          </span>
        )}
      </div>
    </motion.div>
  );
}

/* ── REGISTRATION FORM ──────────────────────────── */
function RegistrationForm({ tab, setTab }) {
  const [data, setData] = useState({
    name: '', email: '', phone: '', message: '',
    domain: '', duration: '', mode: '',
    college: '', level: '', internDomain: '',
    instName: '', instContact: '', instDesig: '',
    instCount: '', instDomain: '', instDuration: '', instMode: '', instDate: '', instMou: '',
  });

  const set = (k, v) => setData((p) => ({ ...p, [k]: v }));

  const inputCls = 'w-full px-4 py-3 bg-[#080F1E] border border-white/15 rounded-xl text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent';
  const labelCls = 'block text-sm font-semibold text-slate-300 mb-2';

  const handleSubmit = (e) => {
    e.preventDefault();
    let subject = '', body = '';
    if (tab === 'training') {
      subject = `Training Enrollment – ${data.name}`;
      body = `Name: ${data.name}%0D%0AEmail: ${data.email}%0D%0APhone: ${data.phone}%0D%0ADomain: ${data.domain}%0D%0AProgram: ${data.duration}%0D%0AMode: ${data.mode}%0D%0A%0D%0AMessage: ${data.message}`;
    } else if (tab === 'internship') {
      subject = `Internship Application – ${data.name}`;
      body = `Name: ${data.name}%0D%0AEmail: ${data.email}%0D%0APhone: ${data.phone}%0D%0ACollege: ${data.college}%0D%0ATrack: ${data.internDomain}%0D%0ALevel: ${data.level}%0D%0A%0D%0AMessage: ${data.message}`;
    } else {
      subject = `Institutional Training Enquiry – ${data.instName}`;
      body = `Institution: ${data.instName}%0D%0AContact: ${data.instContact} (${data.instDesig})%0D%0AEmail: ${data.email}%0D%0APhone: ${data.phone}%0D%0AStudent Count: ${data.instCount}%0D%0ADomain(s): ${data.instDomain}%0D%0APreferred Duration: ${data.instDuration}%0D%0ADelivery Mode: ${data.instMode}%0D%0APreferred Date: ${data.instDate}%0D%0AMOU Interest: ${data.instMou}%0D%0A%0D%0AMessage: ${data.message}`;
    }
    window.location.href = `mailto:officecalloc@gmail.com?subject=${subject}&body=${body}`;
  };

  const tabs = [
    { key: 'training',     label: 'Industrial Training', icon: BookOpen },
    { key: 'internship',   label: 'Internship',          icon: GraduationCap },
    { key: 'institution',  label: 'Institution / College', icon: Building2 },
  ];

  return (
    <section id="register" className="py-24 bg-[#080F1E]">
      <div className="max-w-2xl mx-auto px-6 lg:px-8">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>

          <motion.div variants={fadeUp} className="text-center mb-10">
            <span className="text-orange-500 font-semibold text-sm uppercase tracking-widest">Register / Enquire</span>
            <h2 className="text-4xl font-bold text-white mt-3">
              One Form. <span className="text-orange-500">Every Path.</span>
            </h2>
            <p className="text-slate-400 mt-3">Choose your path below — we'll personalise the next steps and respond within 24 hours.</p>
          </motion.div>

          {/* 3-tab toggle */}
          <motion.div variants={fadeUp}
            className="bg-[#0C1628] border border-white/10 rounded-2xl p-1.5 mb-8 flex gap-1">
            {tabs.map(({ key, label, icon: Icon }) => (
              <button key={key} onClick={() => setTab(key)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-3 rounded-xl text-xs font-semibold transition-all duration-200 leading-tight text-center ${
                  tab === key
                    ? 'bg-orange-500 text-white shadow-md shadow-orange-500/20'
                    : 'text-slate-400 hover:text-orange-400 hover:bg-orange-500/10'
                }`}>
                <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                <span>{label}</span>
              </button>
            ))}
          </motion.div>

          {/* Context hint strip */}
          <motion.div variants={fadeUp}
            className={`mb-6 px-5 py-3.5 rounded-xl text-sm flex items-start gap-3 ${
              tab === 'training'    ? 'bg-orange-500/10 text-orange-400 border border-orange-500/20' :
              tab === 'internship'  ? 'bg-blue-500/10  text-blue-400  border border-blue-500/20'   :
                                      'bg-teal-500/10  text-teal-400  border border-teal-500/20'
            }`}>
            {tab === 'training'   && <BookOpen   className="w-4 h-4 flex-shrink-0 mt-0.5" />}
            {tab === 'internship' && <GraduationCap className="w-4 h-4 flex-shrink-0 mt-0.5" />}
            {tab === 'institution'&& <Building2  className="w-4 h-4 flex-shrink-0 mt-0.5" />}
            <span>
              {tab === 'training'    && 'Enrolling as an individual? Select your domain and preferred duration below.'}
              {tab === 'internship'  && 'Applying for an internship? Tell us your domain interest and current level — limited spots available.'}
              {tab === 'institution' && 'Enquiring for your institution? We offer custom pricing and curriculum for student batches of 30+.'}
            </span>
          </motion.div>

          <motion.div variants={fadeUp} className="bg-[#0C1628] border border-white/10 rounded-3xl p-8">
            <form onSubmit={handleSubmit} className="space-y-5">

              {/* TRAINING FIELDS */}
              {tab === 'training' && (
                <>
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className={labelCls}>Full Name *</label>
                      <input required type="text" value={data.name} onChange={(e) => set('name', e.target.value)} placeholder="Your name" className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls}>Email *</label>
                      <input required type="email" value={data.email} onChange={(e) => set('email', e.target.value)} placeholder="your@email.com" className={inputCls} />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className={labelCls}>Phone *</label>
                      <input required type="tel" value={data.phone} onChange={(e) => set('phone', e.target.value)} placeholder="+91 XXXXXXXXXX" className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls}>Domain of Interest *</label>
                      <select required value={data.domain} onChange={(e) => set('domain', e.target.value)} className={inputCls}>
                        <option value="">Select domain</option>
                        {domains.map((d) => <option key={d.num}>{d.name}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className={labelCls}>Program Duration *</label>
                      <select required value={data.duration} onChange={(e) => set('duration', e.target.value)} className={inputCls}>
                        <option value="">Select duration</option>
                        <option>7 Days – Crash Course</option>
                        <option>15 Days – Short-Term Intensive</option>
                        <option>1 Month – Certificate Program</option>
                        <option>3 Months – Diploma Program</option>
                      </select>
                    </div>
                    <div>
                      <label className={labelCls}>Preferred Mode</label>
                      <select value={data.mode} onChange={(e) => set('mode', e.target.value)} className={inputCls}>
                        <option value="">Select mode</option>
                        <option>In-Campus (Offline)</option>
                        <option>Online / Remote</option>
                        <option>Hybrid (Recommended)</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Message / Questions</label>
                    <textarea rows="3" value={data.message} onChange={(e) => set('message', e.target.value)} placeholder="Any specific requirements or questions..." className={`${inputCls} resize-none`} />
                  </div>
                  <motion.button type="submit" whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                    className="w-full bg-orange-500 hover:bg-orange-600 text-white py-4 rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors">
                    <BookOpen className="w-4 h-4" /> Enroll for Training <ArrowRight className="w-4 h-4" />
                  </motion.button>
                </>
              )}

              {/* INTERNSHIP FIELDS */}
              {tab === 'internship' && (
                <>
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className={labelCls}>Full Name *</label>
                      <input required type="text" value={data.name} onChange={(e) => set('name', e.target.value)} placeholder="Your name" className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls}>Email *</label>
                      <input required type="email" value={data.email} onChange={(e) => set('email', e.target.value)} placeholder="your@email.com" className={inputCls} />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className={labelCls}>Phone *</label>
                      <input required type="tel" value={data.phone} onChange={(e) => set('phone', e.target.value)} placeholder="+91 XXXXXXXXXX" className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls}>College / Institution *</label>
                      <input required type="text" value={data.college} onChange={(e) => set('college', e.target.value)} placeholder="Your college name" className={inputCls} />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className={labelCls}>Internship Track *</label>
                      <select required value={data.internDomain} onChange={(e) => set('internDomain', e.target.value)} className={inputCls}>
                        <option value="">Select track</option>
                        {internshipTracks.map((t) => <option key={t.name}>{t.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className={labelCls}>Your Current Level *</label>
                      <select required value={data.level} onChange={(e) => set('level', e.target.value)} className={inputCls}>
                        <option value="">Select level</option>
                        <option>Beginner (some basics)</option>
                        <option>Intermediate (comfortable with the domain)</option>
                        <option>Advanced (prior projects / work exp.)</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>About You / Goals</label>
                    <textarea rows="3" value={data.message} onChange={(e) => set('message', e.target.value)} placeholder="Tell us a bit about yourself and what you want to achieve..." className={`${inputCls} resize-none`} />
                  </div>
                  <motion.button type="submit" whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-4 rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors">
                    <GraduationCap className="w-4 h-4" /> Apply for Internship <ArrowRight className="w-4 h-4" />
                  </motion.button>
                </>
              )}

              {/* INSTITUTION FIELDS */}
              {tab === 'institution' && (
                <>
                  <div className="grid md:grid-cols-2 gap-5">
                    <div className="md:col-span-2">
                      <label className={labelCls}>Institution / College Name *</label>
                      <input required type="text" value={data.instName} onChange={(e) => set('instName', e.target.value)} placeholder="e.g. ABC Engineering College" className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls}>Contact Person Name *</label>
                      <input required type="text" value={data.instContact} onChange={(e) => set('instContact', e.target.value)} placeholder="Full name" className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls}>Designation / Role *</label>
                      <input required type="text" value={data.instDesig} onChange={(e) => set('instDesig', e.target.value)} placeholder="e.g. HOD, TPO, Principal" className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls}>Official Email *</label>
                      <input required type="email" value={data.email} onChange={(e) => set('email', e.target.value)} placeholder="official@college.edu.in" className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls}>Phone / WhatsApp *</label>
                      <input required type="tel" value={data.phone} onChange={(e) => set('phone', e.target.value)} placeholder="+91 XXXXXXXXXX" className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls}>Expected Student Count *</label>
                      <select required value={data.instCount} onChange={(e) => set('instCount', e.target.value)} className={inputCls}>
                        <option value="">Select batch size</option>
                        <option>Up to 30 students</option>
                        <option>30 – 60 students</option>
                        <option>60 – 120 students</option>
                        <option>120+ students</option>
                      </select>
                    </div>
                    <div>
                      <label className={labelCls}>Domain(s) of Interest *</label>
                      <select required value={data.instDomain} onChange={(e) => set('instDomain', e.target.value)} className={inputCls}>
                        <option value="">Select domain</option>
                        {domains.map((d) => <option key={d.num}>{d.name}</option>)}
                        <option>Multiple Domains (mention in message)</option>
                      </select>
                    </div>
                    <div>
                      <label className={labelCls}>Preferred Duration *</label>
                      <select required value={data.instDuration} onChange={(e) => set('instDuration', e.target.value)} className={inputCls}>
                        <option value="">Select duration</option>
                        <option>7 Days – Workshop / Crash Course</option>
                        <option>15 Days – Short-Term Intensive</option>
                        <option>1 Month – Certificate Program</option>
                        <option>3 Months – Diploma Program</option>
                        <option>Custom / Discuss</option>
                      </select>
                    </div>
                    <div>
                      <label className={labelCls}>Preferred Delivery Mode</label>
                      <select value={data.instMode} onChange={(e) => set('instMode', e.target.value)} className={inputCls}>
                        <option value="">Select mode</option>
                        <option>In-Campus (Offline)</option>
                        <option>Online / Remote</option>
                        <option>Hybrid (Recommended)</option>
                      </select>
                    </div>
                    <div>
                      <label className={labelCls}>Preferred Training Month / Date</label>
                      <input type="text" value={data.instDate} onChange={(e) => set('instDate', e.target.value)} placeholder="e.g. July 2025 or Semester Break" className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls}>MOU / Partnership Interest</label>
                      <select value={data.instMou} onChange={(e) => set('instMou', e.target.value)} className={inputCls}>
                        <option value="">Select</option>
                        <option>Yes – interested in MOU / partnership</option>
                        <option>No – one-time training only</option>
                        <option>Open to discussing</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Additional Requirements / Message</label>
                    <textarea rows="3" value={data.message} onChange={(e) => set('message', e.target.value)} placeholder="Curriculum alignment needs, specific outcomes, accreditation requirements, or any other details..." className={`${inputCls} resize-none`} />
                  </div>

                  {/* Pricing note */}
                  <div className="flex items-start gap-3 bg-teal-500/10 border border-teal-500/20 rounded-xl p-4">
                    <Tag className="w-4 h-4 text-teal-400 flex-shrink-0 mt-0.5" />
                    <p className="text-teal-300 text-xs leading-relaxed">
                      Institutional enquiries receive a <strong className="text-teal-200">custom proposal with pricing within 48 hours</strong> — including per-student cost, curriculum outline, and delivery plan tailored to your batch size.
                    </p>
                  </div>

                  <motion.button type="submit" whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                    className="w-full bg-teal-600 hover:bg-teal-700 text-white py-4 rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors">
                    <Building2 className="w-4 h-4" /> Request Custom Proposal <ArrowRight className="w-4 h-4" />
                  </motion.button>
                </>
              )}

              <p className="text-xs text-slate-500 text-center pt-1">We respond within 24 hours. No spam — only relevant program details.</p>
            </form>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

/* ── PAGE ───────────────────────────────────────── */
export default function TrainingPage() {
  const [formTab, setFormTab] = useState('training');

  const scrollToForm = (tab) => {
    setFormTab(tab);
    setTimeout(() => {
      document.getElementById('register')?.scrollIntoView({ behavior: 'smooth' });
    }, 50);
  };

  return (
    <div className="min-h-screen bg-[#060D1F]">
      <Navbar />

      {/* HERO */}
      <section className="relative pt-20 bg-[#060D1F] overflow-hidden">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-orange-500/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-violet-600/10 rounded-full blur-[80px]" />
        <div className="absolute inset-0 opacity-[0.04]"
          style={{ backgroundImage: 'radial-gradient(circle,#fff 1px,transparent 1px)', backgroundSize: '36px 36px' }} />

        <div className="relative max-w-5xl mx-auto px-6 lg:px-8 pt-20 pb-28 text-center">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.span variants={fadeUp}
              className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/20 text-orange-400 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Zap className="w-4 h-4" /> Industrial Training · Internship · Campus Programs
            </motion.span>
            <motion.h1 variants={fadeUp} className="text-5xl lg:text-7xl font-bold text-white mb-5 leading-tight">
              Learn. <span className="text-orange-500">Build.</span><br />Get Hired.
            </motion.h1>
            <motion.p variants={fadeUp} className="text-slate-400 text-lg max-w-3xl mx-auto mb-10">
              CALLOC delivers industry-aligned technical training across 10 domains, immersive internship programs, and institutional partnerships — for individuals, graduates, and colleges alike.
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-wrap justify-center gap-3 mb-14">
              <button onClick={() => scrollToForm('training')}
                className="bg-orange-500 hover:bg-orange-600 text-white px-7 py-3.5 rounded-xl font-semibold text-sm flex items-center gap-2 transition-colors">
                Enroll for Training <ArrowRight className="w-4 h-4" />
              </button>
              <button onClick={() => scrollToForm('internship')}
                className="bg-blue-600 hover:bg-blue-700 text-white px-7 py-3.5 rounded-xl font-semibold text-sm flex items-center gap-2 transition-colors">
                Apply for Internship
              </button>
              <button onClick={() => scrollToForm('institution')}
                className="bg-white/8 hover:bg-white/14 border border-white/20 text-white px-7 py-3.5 rounded-xl font-semibold text-sm flex items-center gap-2 transition-colors">
                <Building2 className="w-4 h-4" /> Institution Enquiry
              </button>
            </motion.div>
            <motion.div variants={fadeUp} className="flex flex-wrap justify-center gap-10">
              {[
                { val: '500+', label: 'Students Trained' },
                { val: '50+',  label: 'Colleges' },
                { val: '10',   label: 'Tech Domains' },
                { val: '87%',  label: 'Placement Rate' },
              ].map((s) => (
                <div key={s.label} className="text-center">
                  <div className="text-3xl font-bold text-white">{s.val}</div>
                  <div className="text-slate-500 text-sm mt-1">{s.label}</div>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* 10 DOMAINS BENTO GRID */}
      <section id="domains" className="py-24 bg-[#060D1F] relative overflow-hidden">
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-blue-600/8 rounded-full blur-[100px] pointer-events-none" />
        <div className="absolute bottom-20 right-1/4 w-80 h-80 bg-violet-600/8 rounded-full blur-[80px] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-14">
            <motion.span variants={fadeUp} className="text-orange-400 font-semibold text-sm uppercase tracking-widest">10 Domains</motion.span>
            <motion.h2 variants={fadeUp} className="text-4xl lg:text-5xl font-bold text-white mt-3">
              What You'll <span className="text-orange-500">Master</span>
            </motion.h2>
            <motion.p variants={fadeUp} className="text-slate-500 mt-3 max-w-xl mx-auto">
              Hover any domain to feel the glow. Every domain available in all program durations.
            </motion.p>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {domains.map((d) => <DomainCard key={d.num} d={d} />)}
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mt-10">
            <button onClick={() => scrollToForm('training')}
              className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-8 py-3.5 rounded-xl font-semibold text-sm transition-colors">
              Enroll in a Domain <ArrowRight className="w-4 h-4" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* PROGRAM DURATIONS */}
      <section className="py-20 bg-[#080F1E]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-14">
            <motion.span variants={fadeUp} className="text-orange-500 font-semibold text-sm uppercase tracking-widest">Duration Options</motion.span>
            <motion.h2 variants={fadeUp} className="text-4xl font-bold text-white mt-3">Choose Your <span className="text-orange-500">Program</span></motion.h2>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {durations.map((d) => (
              <motion.div key={d.label} variants={fadeUp} whileHover={{ y: -6 }}
                className={`relative rounded-2xl p-7 border-2 transition-all ${d.popular ? 'bg-orange-500 border-orange-400 shadow-xl shadow-orange-500/20' : 'bg-[#0C1628] border-white/10 hover:border-orange-500/40'}`}>
                {d.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-white text-orange-500 text-xs font-bold px-3 py-1 rounded-full shadow whitespace-nowrap">Most Popular</span>
                )}
                <div className={`text-5xl font-black mb-0.5 ${d.popular ? 'text-white' : 'text-orange-400'}`}>{d.days}</div>
                <div className={`text-xl font-bold mb-1 ${d.popular ? 'text-white' : 'text-white'}`}>{d.unit}</div>
                <div className={`font-semibold text-sm mb-1 ${d.popular ? 'text-white/90' : 'text-slate-300'}`}>{d.label}</div>
                <div className={`text-xs ${d.popular ? 'text-white/65' : 'text-slate-500'}`}>{d.sub}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* DELIVERY MODES */}
      <section className="py-20 bg-[#060D1F]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-14">
            <motion.span variants={fadeUp} className="text-orange-500 font-semibold text-sm uppercase tracking-widest">Delivery Flexibility</motion.span>
            <motion.h2 variants={fadeUp} className="text-4xl font-bold text-white mt-3">Three Ways to <span className="text-orange-500">Learn</span></motion.h2>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
            className="grid md:grid-cols-3 gap-6">
            {deliveryModes.map((m) => {
              const Icon = m.icon;
              return (
                <motion.div key={m.label} variants={fadeUp} whileHover={{ y: -5 }}
                  className={`relative bg-[#0C1628] border-2 rounded-2xl p-7 transition-all ${m.badge ? 'border-orange-500/50 shadow-lg shadow-orange-500/10' : 'border-white/10 hover:border-orange-500/40'}`}>
                  {m.badge && <span className="absolute -top-3 left-6 bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full">Recommended</span>}
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5"
                    style={{ background: `${m.color}18`, border: `1px solid ${m.color}35` }}>
                    <Icon className="w-6 h-6" style={{ color: m.color }} />
                  </div>
                  <h3 className="font-bold text-white text-lg mb-3">{m.label}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{m.desc}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* INSTITUTIONAL TRAINING SECTION */}
      <section className="py-24 bg-[#080F1E] relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-teal-500/5 rounded-full blur-[100px] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8">

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-16">
            <motion.span variants={fadeUp}
              className="inline-flex items-center gap-2 bg-teal-500/10 border border-teal-500/20 text-teal-400 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <Building2 className="w-4 h-4" /> For Colleges, Universities & Polytechnics
            </motion.span>
            <motion.h2 variants={fadeUp} className="text-4xl lg:text-5xl font-bold text-white mt-2">
              Are You an <span className="text-teal-400">Institution?</span>
            </motion.h2>
            <motion.p variants={fadeUp} className="text-slate-400 mt-4 text-lg max-w-2xl mx-auto">
              Train your students in bulk — with custom pricing, curriculum aligned to your syllabus, and flexible scheduling that fits your academic calendar.
            </motion.p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12 items-start">

            {/* Left: Benefits grid */}
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
              <motion.h3 variants={fadeUp} className="text-xl font-bold text-white mb-6">
                Why institutions partner with CALLOC
              </motion.h3>
              <div className="space-y-4">
                {institutionalBenefits.map((b) => {
                  const Icon = b.icon;
                  return (
                    <motion.div key={b.title} variants={fadeUp} whileHover={{ x: 4 }}
                      className="flex gap-4 items-start p-5 bg-[#0C1628] border border-white/10 rounded-2xl hover:border-teal-500/30 hover:bg-teal-500/5 transition-all">
                      <div className="w-10 h-10 bg-teal-500/15 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-teal-400" />
                      </div>
                      <div>
                        <h4 className="font-bold text-white text-sm mb-1">{b.title}</h4>
                        <p className="text-slate-400 text-xs leading-relaxed">{b.desc}</p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>

            {/* Right: Dark card */}
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
              <motion.div variants={fadeUp} className="bg-[#060D1F] border border-white/10 rounded-3xl p-8 sticky top-24">
                <div className="mb-6">
                  <span className="inline-block bg-teal-500/15 border border-teal-500/25 text-teal-400 text-xs font-bold px-3 py-1.5 rounded-lg mb-4">
                    Institutional Pricing Available
                  </span>
                  <h3 className="text-2xl font-bold text-white mb-2">Trained at Scale.<br />Trusted by Institutions.</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">
                    From 16-day in-campus bootcamps to 3-month hybrid diploma programs — CALLOC has a flexible, proven model for every institution.
                  </p>
                </div>

                <div className="grid grid-cols-3 gap-4 mb-6">
                  {[
                    { val: '50+',  label: 'Colleges' },
                    { val: '500+', label: 'Students' },
                    { val: '4.8',  label: 'Avg. Rating' },
                  ].map((s) => (
                    <div key={s.label} className="text-center bg-white/5 rounded-xl p-3 border border-white/8">
                      <div className="text-xl font-bold text-white">{s.val}</div>
                      <div className="text-slate-500 text-xs mt-0.5">{s.label}</div>
                    </div>
                  ))}
                </div>

                <div className="mb-6">
                  <p className="text-slate-500 text-xs font-semibold uppercase tracking-wider mb-3">Recent Engagements</p>
                  <div className="space-y-3">
                    {engagements.map((e) => (
                      <div key={e.name} className="flex items-start gap-3 bg-white/5 border border-white/8 rounded-xl p-3.5">
                        <div className="w-8 h-8 bg-teal-500/15 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Building2 className="w-4 h-4 text-teal-400" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-white text-xs font-semibold leading-snug truncate">{e.name}</p>
                          <p className="text-slate-500 text-xs mt-0.5">{e.loc} · {e.type} · {e.duration} · {e.students} students</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <motion.button
                  whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                  onClick={() => scrollToForm('institution')}
                  className="w-full bg-teal-500 hover:bg-teal-600 text-white py-4 rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors">
                  <Building2 className="w-4 h-4" /> Enquire for Institutional Pricing <ArrowRight className="w-4 h-4" />
                </motion.button>
                <p className="text-slate-500 text-xs text-center mt-3">Custom proposal with pricing delivered within 48 hours</p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* INTERNSHIP SECTION HEADER */}
      <div className="bg-[#060D1F] py-14 text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]"
          style={{ backgroundImage: 'radial-gradient(circle,#fff 1px,transparent 1px)', backgroundSize: '32px 32px' }} />
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
          className="relative max-w-3xl mx-auto px-6">
          <motion.span variants={fadeUp} className="text-orange-400 font-semibold text-sm uppercase tracking-widest">Real Work. Real Growth.</motion.span>
          <motion.h2 variants={fadeUp} className="text-4xl lg:text-5xl font-bold text-white mt-3">
            Internship <span className="text-orange-500">Programs</span>
          </motion.h2>
          <motion.p variants={fadeUp} className="text-slate-400 mt-4 text-lg">
            Go beyond learning — ship real software, get mentored by practitioners, and build a portfolio that speaks for itself.
          </motion.p>
        </motion.div>
      </div>

      {/* Training vs Internship comparison */}
      <section className="py-20 bg-[#080F1E]">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-14">
            <motion.span variants={fadeUp} className="text-orange-500 font-semibold text-sm uppercase tracking-widest">Know the Difference</motion.span>
            <motion.h2 variants={fadeUp} className="text-4xl font-bold text-white mt-3">
              Training <span className="text-white/25 font-light mx-2">vs</span> <span className="text-orange-500">Internship</span>
            </motion.h2>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
            className="rounded-3xl overflow-hidden border border-white/10">
            <div className="grid grid-cols-3 bg-[#0C1628]">
              <div className="px-6 py-4 text-sm font-semibold text-slate-500"></div>
              <div className="px-6 py-4 text-center">
                <span className="bg-orange-500/20 text-orange-400 text-sm font-bold px-3 py-1 rounded-lg">Training</span>
              </div>
              <div className="px-6 py-4 text-center">
                <span className="bg-blue-500/20 text-blue-400 text-sm font-bold px-3 py-1 rounded-lg">Internship</span>
              </div>
            </div>
            {vsRows.map((row, i) => (
              <motion.div key={row.label} variants={fadeUp}
                className={`grid grid-cols-3 border-t border-white/8 ${i % 2 === 0 ? 'bg-[#0C1628]' : 'bg-white/5'}`}>
                <div className="px-6 py-4 text-sm font-semibold text-slate-400">{row.label}</div>
                <div className="px-6 py-4 text-sm text-slate-300 flex items-start gap-2">
                  <Check className="w-4 h-4 text-orange-400 flex-shrink-0 mt-0.5" /> {row.training}
                </div>
                <div className="px-6 py-4 text-sm text-slate-300 flex items-start gap-2">
                  <Check className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" /> {row.internship}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* How Internship Works */}
      <section className="py-20 bg-[#060D1F]">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-14">
            <motion.span variants={fadeUp} className="text-orange-500 font-semibold text-sm uppercase tracking-widest">The Process</motion.span>
            <motion.h2 variants={fadeUp} className="text-4xl font-bold text-white mt-3">How the <span className="text-orange-500">Internship Works</span></motion.h2>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
            className="grid md:grid-cols-5 gap-4">
            {internshipSteps.map((s, i) => (
              <motion.div key={s.n} variants={fadeUp} className="relative">
                {i < internshipSteps.length - 1 && (
                  <div className="hidden md:block absolute top-7 h-px border-t-2 border-dashed border-orange-500/30 z-0"
                    style={{ left: '2rem', width: 'calc(100% - 0.5rem)' }} />
                )}
                <div className="relative z-10 text-center">
                  <div className="w-14 h-14 bg-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-orange-500/20">
                    <span className="text-white font-black text-base">{s.n}</span>
                  </div>
                  <h3 className="font-bold text-white text-sm mb-2">{s.title}</h3>
                  <p className="text-slate-400 text-xs leading-relaxed">{s.desc}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Outcomes */}
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
            className="mt-14 bg-[#0C1628] border border-white/10 rounded-2xl p-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Star,  val: '2–3',    label: 'Live Projects',         sub: 'per intern' },
              { icon: Award, val: '100%',   label: 'Certificate + LoR',     sub: 'on completion' },
              { icon: Users, val: '87%',    label: 'Placement Rate',        sub: 'of past interns' },
              { icon: Clock, val: '24 hrs', label: 'Mentor Response Time',  sub: 'guaranteed' },
            ].map((o) => {
              const Icon = o.icon;
              return (
                <motion.div key={o.label} variants={fadeUp} className="text-center">
                  <div className="w-10 h-10 bg-orange-500/15 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <Icon className="w-5 h-5 text-orange-400" />
                  </div>
                  <div className="text-2xl font-bold text-white mb-0.5">{o.val}</div>
                  <div className="text-sm font-semibold text-white/80">{o.label}</div>
                  <div className="text-xs text-slate-500">{o.sub}</div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Internship Tracks */}
      <section className="py-20 bg-[#080F1E]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-14">
            <motion.span variants={fadeUp} className="text-orange-500 font-semibold text-sm uppercase tracking-widest">Available Now</motion.span>
            <motion.h2 variants={fadeUp} className="text-4xl font-bold text-white mt-3">Internship <span className="text-orange-500">Tracks</span></motion.h2>
            <motion.p variants={fadeUp} className="text-slate-400 mt-3 max-w-xl mx-auto">Limited spots per batch. Apply below to secure your place.</motion.p>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {internshipTracks.map((t) => {
              const Icon = t.icon;
              return (
                <motion.div key={t.name} variants={fadeUp} whileHover={{ y: -6 }}
                  className="group bg-[#0C1628] border border-white/10 rounded-2xl p-6 hover:border-blue-500/40 transition-all">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform"
                    style={{ background: `${t.color}18`, border: `1px solid ${t.color}35` }}>
                    <Icon className="w-6 h-6" style={{ color: t.color }} />
                  </div>
                  <h3 className="font-bold text-white mb-2 leading-snug">{t.name}</h3>
                  <div className="flex gap-3 text-xs text-slate-400 mb-4">
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {t.duration}</span>
                    <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {t.spots} spots</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {t.skills.map((sk) => (
                      <span key={sk} className="text-xs px-2 py-0.5 rounded-md font-medium bg-white/8 text-slate-400">{sk}</span>
                    ))}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold px-2.5 py-1 rounded-lg"
                      style={{ background: `${t.color}18`, color: t.color }}>{t.level}</span>
                    <button onClick={() => scrollToForm('internship')}
                      className="text-blue-400 text-xs font-semibold flex items-center gap-1 hover:gap-2 transition-all">
                      Apply <ArrowRight className="w-3 h-3" />
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* REGISTRATION FORM */}
      <RegistrationForm tab={formTab} setTab={setFormTab} />

      {/* CTA */}
      <section className="py-16 bg-[#060D1F]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.h3 variants={fadeUp} className="text-3xl font-bold text-white mb-3">Still have questions?</motion.h3>
            <motion.p variants={fadeUp} className="text-slate-400 mb-8">
              Reach out directly — we'll help you find the right program, pricing, and path.
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-wrap justify-center gap-4">
              <a href="mailto:officecalloc@gmail.com"
                className="flex items-center gap-2 bg-white/10 border border-white/20 text-white px-6 py-3 rounded-xl font-semibold text-sm hover:bg-white/15 transition-colors">
                <Mail className="w-4 h-4 text-orange-400" /> officecalloc@gmail.com
              </a>
              <a href="tel:+918971504091"
                className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-xl font-semibold text-sm transition-colors">
                <Phone className="w-4 h-4" /> +91 8971504091
              </a>
            </motion.div>
            <motion.p variants={fadeUp} className="text-slate-500 text-xs mt-8">
              CALLOC Technologies Pvt. Ltd. · CIN: U72900KA2023PTC182046 · GST: 29AALCC4378D1ZE · Mysore, Karnataka
            </motion.p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
